import { Badge, BasePagination, ChakraSelect, IconButton, Text, Tooltip, VStack } from '@elkaso-app/web-design';
import { useGetCrmMasterCuisinesApi } from 'apis/crm-cuisines/use-get-crm-master-cuisines-api';
import {
  getCrmSourceCuisineMappingsEndpointIdentifier,
  ICrmSourceCuisineMappingsData,
  useGetCrmSourceCuisineMappingsApi,
} from 'apis/crm-cuisines/use-get-crm-source-cuisine-mappings-api';
import { useUpdateCrmSourceCuisineMappingApi } from 'apis/crm-cuisines/use-update-crm-source-cuisine-mapping-api';
import { BaseSortButton, BaseTable, BaseTableContainer } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  TableHeader,
} from 'components/base-table/components';
import { usePageParams } from 'hooks/use-page-params';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import React, { useEffect, useState } from 'react';
import { FiCheck } from 'react-icons/fi';
import { useQueryClient } from 'react-query';
import { CellProps } from 'react-table';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';
import { SourceNameAndPlatformFilterButton } from '../components/filters/source-name-and-platform-filter';

// Components for table cells
const CuisineNameCell: React.FC<{ name: string; platform: string }> = ({ name, platform }) => (
  <div style={{ height: '40px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
    <VStack align='start' spacing={1}>
      <Text fontWeight='semibold' fontSize='md' color='gray.800' lineHeight='1.2'>
        {name}
      </Text>
      <Badge colorScheme='blue' variant='subtle' size='sm' borderRadius='md'>
        {platform}
      </Badge>
    </VStack>
  </div>
);

const MasterCuisineMappingCell: React.FC<{
  mappingId: number;
  selectedMasterCuisineId: number | null;
  onMasterCuisineChange: (mappingId: number, masterCuisineId: number | null) => void;
}> = ({ mappingId, selectedMasterCuisineId, onMasterCuisineChange }) => {
  const { data: masterCuisines, isLoading: isMasterCuisinesLoading } = useGetCrmMasterCuisinesApi();

  // Format master cuisines for ChakraSelect
  const cuisineOptions =
    masterCuisines?.map((cuisine) => ({
      value: cuisine.id,
      label: cuisine.name,
    })) || [];

  const selectedOption = cuisineOptions.find((option) => option.value === selectedMasterCuisineId) || null;

  return (
    <div
      style={{
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        width: '280px',
        minWidth: '280px',
        maxWidth: '280px',
      }}>
      <div
        style={{
          width: '100%',
          minWidth: '280px',
          maxWidth: '280px',
        }}>
        <ChakraSelect
          value={selectedOption}
          onChange={(option: any) => onMasterCuisineChange(mappingId, option?.value || null)}
          options={cuisineOptions || []}
          size='sm'
          placeholder='Search and select cuisine...'
          isClearable
          isSearchable
          isLoading={isMasterCuisinesLoading}
          menuPlacement='auto'
          menuPortalTarget={document.body}
        />
      </div>
    </div>
  );
};

const ConfidenceCell: React.FC<{
  confidence: number;
  mappingId: number;
  onConfidenceChange: (mappingId: number, confidence: number) => void;
}> = ({ confidence, mappingId, onConfidenceChange }) => {
  const confidenceOptions = [
    { value: 100, label: '100% - Perfect match' },
    { value: 90, label: '90% - Very confident' },
    { value: 80, label: '80% - Confident' },
    { value: 70, label: '70% - Somewhat confident' },
    { value: 60, label: '60% - Uncertain' },
  ];

  const selectedOption = confidenceOptions.find((option) => option.value === confidence) || null;

  return (
    <div
      style={{
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        width: '220px',
        minWidth: '220px',
        maxWidth: '220px',
      }}>
      <div
        style={{
          width: '100%',
          minWidth: '220px',
          maxWidth: '220px',
        }}>
        <ChakraSelect
          value={selectedOption}
          onChange={(option: any) => onConfidenceChange(mappingId, option?.value || 60)}
          options={confidenceOptions}
          size='sm'
          menuPlacement='auto'
          menuPortalTarget={document.body}
        />
      </div>
    </div>
  );
};

const ActionsCell: React.FC<{
  mappingId: number;
  masterCuisineId: number | null;
  confidence: number | null;
  isVerified: boolean;
  onApprove: (mappingId: number) => void;
}> = ({ mappingId, masterCuisineId, confidence, isVerified, onApprove }) => {
  const hasRequiredSelections = masterCuisineId && confidence;

  const getTooltipLabel = () => {
    if (isVerified && hasRequiredSelections) return 'Update mapping';
    if (isVerified) return 'Select master cuisine and confidence to update';
    if (hasRequiredSelections) return 'Approve';
    if (!masterCuisineId && !confidence) return 'Select master cuisine and confidence';
    if (!masterCuisineId) return 'Select master cuisine';
    return 'Select confidence level';
  };

  return (
    <div style={{ height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Tooltip label={getTooltipLabel()}>
        <IconButton
          aria-label={isVerified ? 'Update mapping' : 'Approve mapping'}
          icon={<FiCheck />}
          size='sm'
          colorScheme='green'
          variant={isVerified ? 'solid' : 'outline'}
          isDisabled={!hasRequiredSelections}
          onClick={() => onApprove(mappingId)}
        />
      </Tooltip>
    </div>
  );
};

const CrmCuisinesListScreen = () => {
  const setPaginationParams = useSetPaginationParamsV1();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const updateMappingMutation = useUpdateCrmSourceCuisineMappingApi();
  const { getPageParams } = usePageParams();

  // Get filter parameters from URL
  const params = getPageParams();
  const { name, sourcePlatform } = params;

  // State for temporary changes
  const [tempMasterCuisines, setTempMasterCuisines] = useState<Record<number, number>>({});
  const [tempConfidenceScores, setTempConfidenceScores] = useState<Record<number, number>>({});

  const { isLoading, isFetching, data } = useGetCrmSourceCuisineMappingsApi({
    name: (name as string) || undefined,
    sourcePlatform: (sourcePlatform as string) || undefined,
  });
  const { pageCount, total, data: mappingsData } = data ?? {};

  const handleMasterCuisineChange = (mappingId: number, masterCuisineId: number | null) => {
    setTempMasterCuisines((prev) => {
      if (masterCuisineId === null) {
        const newState = { ...prev };
        delete newState[mappingId];
        return newState;
      }
      return {
        ...prev,
        [mappingId]: masterCuisineId,
      };
    });
  };

  const handleConfidenceChange = (mappingId: number, confidence: number) => {
    setTempConfidenceScores((prev) => ({
      ...prev,
      [mappingId]: confidence,
    }));
  };

  const handleApprove = async (mappingId: number) => {
    const mapping = mappingsData?.find((m) => m.id === mappingId);
    if (!mapping) return;

    const masterCuisineId = tempMasterCuisines[mappingId] || mapping.masterCuisine?.id || null;
    if (!masterCuisineId) return;

    const confidencePercentage = tempConfidenceScores[mappingId] || mapping.mappingConfidence * 100;
    // Convert from percentage (0-100) to decimal (0.00-1.00)
    const confidenceScore = confidencePercentage / 100;

    try {
      await updateMappingMutation.mutateAsync({
        id: mappingId,
        body: {
          masterCuisineId,
          confidenceScore,
          isVerified: true,
        },
      });

      // Remove from temp state
      setTempMasterCuisines((prev) => {
        const newState = { ...prev };
        delete newState[mappingId];
        return newState;
      });

      setTempConfidenceScores((prev) => {
        const newState = { ...prev };
        delete newState[mappingId];
        return newState;
      });

      // Refresh data
      queryClient.invalidateQueries([getCrmSourceCuisineMappingsEndpointIdentifier]);
    } catch (error) {
      console.error('Error approving/updating mapping:', error);
    }
  };

  useEffect(() => {
    dispatch(changeTitle('CRM Cuisines'));
  }, [dispatch]);

  const columns = React.useMemo(
    () => [
      {
        Header: (
          <TableHeader
            name='Cuisine Name'
            sortButtonComponent={<BaseSortButton sortBy='name' />}
            filterButtonComponent={<SourceNameAndPlatformFilterButton />}
          />
        ),
        accessor: 'cuisineName',
        width: '25%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={CuisineNameCell} {...value} />,
      },
      {
        Header: <TableHeader name='Map to Master Cuisine' />,
        accessor: 'masterCuisineMapping',
        width: '30%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={MasterCuisineMappingCell} {...value} />,
      },
      {
        Header: <TableHeader name='Confidence' />,
        accessor: 'confidence',
        width: '25%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={ConfidenceCell} {...value} />,
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        width: '20%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={ActionsCell} {...value} />,
      },
    ],
    []
  );

  const tableData = React.useMemo(
    () =>
      mappingsData?.map((item: ICrmSourceCuisineMappingsData) => ({
        id: item.id,
        cuisineName: {
          name: item.sourceName,
          platform: item.sourcePlatform,
        },
        masterCuisineMapping: {
          mappingId: item.id,
          selectedMasterCuisineId: tempMasterCuisines[item.id] || item.masterCuisine?.id || null,
          onMasterCuisineChange: handleMasterCuisineChange,
        },
        confidence: {
          confidence: tempConfidenceScores[item.id] || item.mappingConfidence * 100,
          mappingId: item.id,
          onConfidenceChange: handleConfidenceChange,
        },
        actions: {
          mappingId: item.id,
          masterCuisineId: tempMasterCuisines[item.id] || item.masterCuisine?.id || null,
          confidence: tempConfidenceScores[item.id] || item.mappingConfidence * 100,
          isVerified: item.isVerified,
          onApprove: handleApprove,
        },
      })) || [],
    [mappingsData, tempMasterCuisines, tempConfidenceScores]
  );

  return (
    <BaseTableContainer isLoading={isLoading}>
      <BaseTableContainer.Header>
        <BaseTableContainer.Header.Left>
          <BaseTableTitle title='CRM Cuisines' />
          <BaseTableSpinner isFetching={isFetching} />
        </BaseTableContainer.Header.Left>

        <BaseTableContainer.Header.Right>
          <ClearFilterButton />
        </BaseTableContainer.Header.Right>
      </BaseTableContainer.Header>

      <BaseTableContainer.TableWrapper>
        <BaseTable columns={columns} data={tableData} />
      </BaseTableContainer.TableWrapper>

      <BaseTableContainer.Footer>
        <BaseTableContainer.Footer.Left />
        <BaseTableContainer.Footer.Right>
          <BasePagination>
            <BasePagination.Pages pagesCount={pageCount ?? 0} setSearchParams={setPaginationParams} />
            <BasePagination.Limit total={total ?? 0} setSearchParams={setPaginationParams} />
          </BasePagination>
        </BaseTableContainer.Footer.Right>
      </BaseTableContainer.Footer>
    </BaseTableContainer>
  );
};

export default CrmCuisinesListScreen;
